<script setup>
import { computed } from 'vue'

const props = defineProps({
  /** Renders an <a> when set, a <button> otherwise. */
  href: { type: String, default: null },
  variant: {
    type: String,
    default: 'primary',
    validator: (value) => ['primary', 'ink', 'outline'].includes(value)
  },
  size: {
    type: String,
    default: 'md',
    validator: (value) => ['sm', 'md', 'lg'].includes(value)
  },
  type: {
    type: String,
    default: 'button',
    validator: (value) => ['button', 'submit', 'reset'].includes(value)
  },
  disabled: { type: Boolean, default: false }
})

// White-on-brand is only 4.34:1; white-on-brand-deep is 6.51:1, so the filled
// button rests on the deeper green and darkens further on hover.
const VARIANTS = {
  primary:
    'bg-brand-deep text-white shadow-[0_10px_30px_-12px_rgba(15,138,99,.6)] hover:bg-brand-deeper',
  ink: 'bg-ink text-white hover:bg-brand-deep',
  outline: 'border border-hairline bg-white text-ink hover:border-ink'
}

const SIZES = {
  sm: 'px-4 py-2 text-sm',
  md: 'px-[26px] py-[14px] text-[15px]',
  lg: 'px-[34px] py-4 text-base'
}

const classes = computed(() => [
  'inline-flex items-center justify-center rounded-[11px] font-semibold transition-colors duration-300',
  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand',
  VARIANTS[props.variant],
  SIZES[props.size],
  props.disabled ? 'pointer-events-none opacity-60' : ''
])
</script>

<template>
  <a v-if="href" :href="href" :class="classes">
    <slot />
  </a>
  <button v-else :type="type" :disabled="disabled" :class="classes">
    <slot />
  </button>
</template>
